// This file is the expanded chat view component containing the search bar and the chat messages container.

import type React from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { track } from '../analytics';
import type { MessageSourceType } from '../analytics/types';
import {
    BOOKING_COMPLETED_MARKER,
    type ExpandedLayout,
    type FormFactor,
    getSearchBarPlaceholder,
} from '../config/constants';
import { getDeploymentTarget, isChromeIndicatorHidden } from '../config/deployment';
import {
    getSiteCTAs,
    isCurrentPageCTADestination,
    isInlineCTAOnDemoEnabled,
    selectStripCTAs,
} from '../config/SiteConfigManager';
import { useChatLanguage } from '../context/ChatThemeContext';
import { useVisualViewportBottomOffset } from '../hooks/ui/useVisualViewportBottomOffset';
import type { FlowActions, FlowState } from '../hooks/widget/useConversationFlowReducer';
import type { ConversationPair } from '../services/chatStorage';
import type { BookingContext } from '../services/inboundx';
import { copyTranscript } from '../utils/content/transcript';
import { logger } from '../utils/core/logger';
import { isMobileDevice } from '../utils/device/mobileDetection';
import { tryOpenBookingPopup } from '../utils/web/bookingPopup';
import { appendCtaAttributionParams } from '../utils/web/ctaUrlAttribution';
import { appendEmailToUrl } from '../utils/web/urlUtils';
import BookingTopBar from './ExpandedChat/BookingTopBar';
import ExpandedChatAnimationStyles from './ExpandedChat/ExpandedChatAnimationStyles';
import ExpandedChatContainer from './ExpandedChat/ExpandedChatContainer';
import ExpandedChatHeader from './ExpandedChat/ExpandedChatHeader';
import PostConversionTopBar from './ExpandedChat/PostConversionTopBar';
import InChatCalendarEmbed, { getInChatCalendarRenderedHeight } from './InChatCalendarEmbed';
import SearchBar from './SearchBar';
import BookingEmailDisclaimer from './searchbar/BookingEmailDisclaimer';

interface ExpandedChatViewProps {
    conversationPairs: ConversationPair[];
    streamingMessage?: {
        id: string;
        content: string;
        isComplete: boolean;
        isThinking: boolean;
        suggestedFollowUps?: string[];
    };
    onSendMessage: (
        messageToSend: string,
        messageSource?: MessageSourceType,
        bookingContext?: BookingContext
    ) => void;
    isLoading: boolean;
    primaryColor: string;
    siteConfig: { name: string };
    messagesEndRef: React.RefObject<HTMLDivElement>;
    // IX-4266: rendered as the header's "New conversation" control, gated on
    // there being a conversation to reset.
    onResetConversation: () => void;
    onMinimize: () => void;
    evaluationEnabled?: boolean;
    domain: string;
    onLatestQuestionRef?: (ref: HTMLElement | null) => void;
    zIndex?: number; // Custom z-index from site config - defaults to 9999
    onRetry?: () => void;
    // Conversation flow state (booking, redirecting, answering + visitor profile)
    flowState: FlowState;
    flowActions: FlowActions;
    isFormAssistant?: boolean;
    formFactor?: FormFactor; // Widget form factor - 'default' or 'copilot'
    viewLayout?: ExpandedLayout;
    onToggleLayout?: () => void;
    onExitCopilotMode?: () => void;
    isPostConversion?: boolean;
    onEngage?: () => void;
    /**
     * Playground device-preview only: simulated viewport width. When set, the mobile
     * full-height layout switch uses this instead of window.innerWidth (which, inside
     * the preview iframe, would report the host backoffice window). Undefined in prod.
     */
    previewViewportWidth?: number;
}

/**
 * CRITICAL HEIGHT SYSTEM DOCUMENTATION
 *
 * The ExpandedChatView uses CSS-based responsive height handling:
 *
 * 1. Container positioning: bottom: 0px - container sits at the bottom of screen
 * 2. Height handling:
 *    - Mobile (≤1242px): visualViewport.height px when available; otherwise 100dvh (fallback 100vh)
 *    - Desktop: min(max(67vh, 536px), 600px)
 * 3. Container bottom padding: 24px - reserves space for input area at bottom
 * 4. SearchBar positioning: Within the container, positioned 24px from screen bottom
 *
 * These values work together to ensure:
 * - Full-screen experience on mobile devices
 * - Browser automatically adjusts viewport height when keyboard appears
 * - Input area has proper spacing and positioning
 * - SearchBar is exactly 24px from the bottom of the screen
 * - Modern glass effect design from SearchBar component
 * - Scrolling behavior remains consistent across devices
 *
 * ⚠️  WARNING: Changing these values will affect conversation message scrolling behavior
 */

/** Search backwards through conversation pairs for the most recent profilingProgress.
 *  The latest pair may not have an answer yet while streaming, so we scan from the end. */
function getLatestProfilingProgress(pairs: ConversationPair[]) {
    for (let i = pairs.length - 1; i >= 0; i--) {
        const p = pairs[i]?.answer?.profilingProgress;
        if (p) return p;
    }
    return undefined;
}

const ExpandedChatView: React.FC<ExpandedChatViewProps> = ({
    conversationPairs,
    streamingMessage,
    onSendMessage,
    isLoading,
    primaryColor,
    siteConfig,
    messagesEndRef,
    onMinimize,
    onResetConversation,
    evaluationEnabled = true,
    domain,
    onLatestQuestionRef,
    zIndex = 9999,
    onRetry,
    flowState,
    isFormAssistant = false,
    flowActions,
    formFactor = 'default',
    viewLayout = 'expanded',
    onToggleLayout,
    onExitCopilotMode,
    isPostConversion = false,
    onEngage,
    previewViewportWidth,
}) => {
    // Render the layout that is currently animating in/out. When the prop `viewLayout`
    // changes (toggle), we keep `displayedViewLayout` on the previous value while the
    // exit keyframe plays, then swap to the new value and play the entrance keyframe.
    const [displayedViewLayout, setDisplayedViewLayout] = useState(viewLayout);
    const isCopilotLayout = displayedViewLayout === 'copilot';
    const prevPropViewLayoutRef = useRef(viewLayout);

    const [animationState, setAnimationState] = useState<'entering' | 'visible' | 'exiting'>(
        'entering'
    );

    // Layout toggle: chain exit keyframe (old layout) → swap → entrance keyframe (new layout).
    useEffect(() => {
        if (prevPropViewLayoutRef.current === viewLayout) return;
        prevPropViewLayoutRef.current = viewLayout;

        setAnimationState('exiting');

        const exitMs = 400; // matches minimizeOut / minimizeOutCopilot keyframe duration
        const enterMs = viewLayout === 'copilot' ? 600 : 500; // matches maximizeIn{,Copilot}

        const swapTimer = setTimeout(() => {
            setDisplayedViewLayout(viewLayout);
            setAnimationState('entering');
        }, exitMs);

        const visibleTimer = setTimeout(() => {
            setAnimationState('visible');
        }, exitMs + enterMs);

        return () => {
            clearTimeout(swapTimer);
            clearTimeout(visibleTimer);
        };
    }, [viewLayout]);
    const [shouldShowCTAs, setShouldShowCTAs] = useState(false);
    const [containerHeight, setContainerHeight] = useState<string | undefined>(undefined);
    const visualViewportBottomOffset = useVisualViewportBottomOffset();
    const viewportResizeKey = `${containerHeight ?? ''}:${visualViewportBottomOffset}`;
    // The expanded search shell is rendered as a bottom overlay so its glass corners
    // reveal the scrolling chat beneath it instead of the white panel behind it.
    // Because that shell height changes with CTAs, disclaimer, wrapping, and responsive
    // width, we measure it and pass the live height into the messages scroller.
    const [inputAreaHeight, setInputAreaHeight] = useState<number>(0);
    const inputAreaRef = useRef<HTMLDivElement>(null);
    // In-chat calendar embed state (from conversation flow reducer)
    const inChatEmbedUrl = flowState.inChatEmbedUrl;
    const layoutBeforeEmbed = flowState.layoutBeforeEmbed;
    // Get site CTAs synchronously from context
    const siteCTAs = useMemo(() => getSiteCTAs(domain), [domain]);
    const inlineCTAOnDemo = useMemo(() => isInlineCTAOnDemoEnabled(domain), [domain]);

    const getPostBookingRedirectUrl = useCallback(
        (ctaId: string | null | undefined): string | undefined => {
            if (!ctaId) return undefined;
            return siteCTAs.find((cta) => cta.cta_id === ctaId)?.post_booking_redirect_url;
        },
        [siteCTAs]
    );

    // Detect if the latest message contains the demo offer emoji (👇).
    // When detected and inline_cta_on_demo is enabled, hide the search-bar CTA strip
    // and show the inline CTA flow instead.
    const lastMessageHasDemoOffer = useMemo(() => {
        const lastPair = conversationPairs[conversationPairs.length - 1];
        // Never show demo CTA right after a completed booking
        if (lastPair?.question?.content === BOOKING_COMPLETED_MARKER) return false;
        const lastContent = lastPair?.answer?.content;
        // Also check streaming message if it's complete
        const streamingContent = streamingMessage?.isComplete ? streamingMessage.content : null;
        const contentToCheck = streamingContent || lastContent;
        return contentToCheck ? contentToCheck.includes('👇') : false;
    }, [conversationPairs, streamingMessage]);

    // Regular CTAs plus the ones gated on the latest answer's intent (e.g. support).
    const lastAnswerIntent = conversationPairs[conversationPairs.length - 1]?.answer?.intent;
    const stripCTAs = useMemo(
        () => selectStripCTAs(siteCTAs, lastAnswerIntent),
        [siteCTAs, lastAnswerIntent]
    );

    // Modes where demo CTAs are never relevant (demo already booked or not applicable)
    const ctasDisabledByMode = isFormAssistant || isPostConversion;

    // Compute whether to hide CTAs (inline CTAs in messages)
    // Hide when: 1) in booking flow awaiting email, OR 2) on a CTA destination page
    // Note: Recompute each render to handle SPA navigation (URL changes without domain change)
    const isOnCTADestinationPage = isCurrentPageCTADestination(domain);
    const hideInlineCTAs = flowState.bookingDetails?.isAwaitingEmail || isOnCTADestinationPage;

    useLayoutEffect(() => {
        if (isCopilotLayout) {
            setInputAreaHeight(0);
            return;
        }

        const inputAreaElement = inputAreaRef.current;
        if (!inputAreaElement) return;

        const measureHeight = () => {
            setInputAreaHeight(inputAreaElement.offsetHeight);
        };

        measureHeight();

        if (typeof ResizeObserver === 'undefined') return;

        // The shell height is dynamic, so keep the scroll inset in sync when
        // CTA visibility, wrapping, or responsive width changes its size.
        const resizeObserver = new ResizeObserver(() => {
            measureHeight();
        });

        resizeObserver.observe(inputAreaElement);

        return () => {
            resizeObserver.disconnect();
        };
    }, [isCopilotLayout]);

    // Note: Snitcher Radar SDK is now loaded in RoseWidget.tsx to ensure
    // session ID is available for first message from collapsed view

    // =================================================================
    // BOOKING FLOW HANDLERS
    // =================================================================

    // Holds a booking start that was requested while an answer was still
    // streaming; flushed once streaming completes (see effect after handler).
    const pendingBookingRef = useRef<{
        ctaId: string;
        redirectUrl: string;
        bookingMessage: string;
        ctaAction?: 'navigate' | 'popup' | 'in-chat';
    } | null>(null);

    /**
     * Handle booking flow start when CTA is clicked and email is not known.
     * This hides CTAs, starts the booking flow state, and sends the booking message to backend.
     */
    const handleBookingFlowStart = useCallback(
        (
            ctaId: string,
            redirectUrl: string,
            bookingMessage: string,
            ctaAction?: 'navigate' | 'popup' | 'in-chat'
        ) => {
            logger.debug('📧 [Booking Flow] Starting booking flow', {
                ctaId,
                redirectUrl,
                bookingMessage,
                ctaAction,
            });

            // Fast path: for in-chat embed when email is already known, open immediately
            // without re-sending a booking message to the backend.
            const email = flowState.visitorProfile?.email;
            if (ctaAction === 'in-chat' && email && email !== 'unknown' && email.includes('@')) {
                logger.debug(
                    '📧 [Booking Flow] Email already known, opening in-chat embed directly'
                );
                setShouldShowCTAs(false);
                const urlWithEmail = appendEmailToUrl(redirectUrl, email);
                if (isCopilotLayout && onToggleLayout) {
                    onToggleLayout();
                }
                flowActions.showInChatEmbed(
                    urlWithEmail,
                    isCopilotLayout ? 'copilot' : undefined,
                    getPostBookingRedirectUrl(ctaId)
                );
                return;
            }

            // The booking message send is dropped by RoseWidget while a previous
            // answer is still streaming (isBusy guard). If we hid CTAs / started
            // the flow now, the booking would never start AND the CTA would be
            // gone — a dead end. Defer until streaming completes (flushed below).
            const isStreaming = streamingMessage && !streamingMessage.isComplete;
            if (isStreaming) {
                logger.debug('📧 [Booking Flow] Streaming in progress, queueing booking start');
                pendingBookingRef.current = { ctaId, redirectUrl, bookingMessage, ctaAction };
                return;
            }

            // 1. Hide CTAs during booking flow
            setShouldShowCTAs(false);

            // 2. Start booking flow via reducer action
            flowActions.startBookingFlow(ctaId, redirectUrl, bookingMessage, ctaAction);
            logger.debug('📧 [Booking Flow] Called startBookingFlow');

            // 3. Send the booking message to backend WITH BOOKING CONTEXT
            const bookingContext: BookingContext = {
                is_booking_flow: true,
                cta_id: ctaId,
                redirect_url: redirectUrl,
            };
            onSendMessage(bookingMessage, 'booking_cta', bookingContext);
        },
        [
            flowActions,
            flowState.visitorProfile?.email,
            streamingMessage,
            isCopilotLayout,
            onToggleLayout,
            onSendMessage,
            getPostBookingRedirectUrl,
        ]
    );

    // Flush a booking start that was queued because an answer was streaming when
    // the CTA was clicked. Re-invokes the handler once streaming completes.
    useEffect(() => {
        const isStreaming = streamingMessage && !streamingMessage.isComplete;
        if (isStreaming || !pendingBookingRef.current) return;
        const pending = pendingBookingRef.current;
        pendingBookingRef.current = null;
        handleBookingFlowStart(
            pending.ctaId,
            pending.redirectUrl,
            pending.bookingMessage,
            pending.ctaAction
        );
    }, [streamingMessage, handleBookingFlowStart]);

    /**
     * Monitor visitor email during booking flow and redirect when ready.
     *
     * Guards:
     * - bookingResponseReceived: ensures the backend has confirmed booking_state.is_booking_flow
     *   before allowing redirect. Prevents premature redirects when email is already known.
     * - isStreaming: wait for streaming to complete before considering redirect.
     */
    useEffect(() => {
        const booking = flowState.bookingDetails;
        const email = flowState.visitorProfile?.email;
        const isStreaming = streamingMessage && !streamingMessage.isComplete;

        logger.debug('📧 [Booking Flow] useEffect triggered', {
            isAwaitingEmail: booking?.isAwaitingEmail,
            pendingRedirectUrl: booking?.pendingRedirectUrl,
            hasVisitorEmail: !!email,
            isStreaming,
        });

        // Only proceed if in booking flow awaiting email
        if (!booking?.isAwaitingEmail || !booking?.pendingRedirectUrl) {
            logger.debug('📧 [Booking Flow] Not in booking flow, skipping');
            return;
        }

        // Wait for streaming to complete before considering redirect
        if (isStreaming) {
            logger.debug('📧 [Booking Flow] Still streaming, waiting for completion');
            return;
        }

        // Wait for backend to confirm booking response before allowing redirect
        if (!booking.bookingResponseReceived) {
            logger.debug('📧 [Booking Flow] Waiting for booking response from backend');
            return;
        }

        const hasValidEmail = email && email !== 'unknown' && email.includes('@');

        logger.debug('📧 [Booking Flow] Email check', {
            hasEmail: !!email,
            email,
            hasValidEmail,
        });

        if (hasValidEmail) {
            logger.debug('📧 [Booking Flow] Email captured, scheduling redirect', {
                redirectUrl: booking.pendingRedirectUrl,
            });

            const redirectTimer = setTimeout(() => {
                // If the backend already built the full URL (with email + custom fields),
                // don't double-append email.
                const pendingRedirectUrl = booking.pendingRedirectUrl;
                if (!pendingRedirectUrl) return;

                let finalUrl = pendingRedirectUrl.includes('email=')
                    ? pendingRedirectUrl
                    : appendEmailToUrl(pendingRedirectUrl, email);

                finalUrl = appendCtaAttributionParams(finalUrl, domain);

                // In-chat mode: embed calendar iframe inside the widget
                if (booking.ctaAction === 'in-chat') {
                    logger.debug('📧 [Booking Flow] Opening as in-chat embed', {
                        redirectUrl: finalUrl,
                    });
                    // If copilot, switch to expanded for adequate space
                    if (isCopilotLayout && onToggleLayout) {
                        onToggleLayout();
                    }
                    flowActions.showInChatEmbed(
                        finalUrl,
                        isCopilotLayout ? 'copilot' : undefined,
                        getPostBookingRedirectUrl(booking.pendingCtaId)
                    );
                    flowActions.completeBookingFlow();
                    return;
                }

                // Popup mode: open provider SDK overlay instead of navigating away
                if (booking.ctaAction === 'popup' && tryOpenBookingPopup(finalUrl)) {
                    logger.debug('📧 [Booking Flow] Opened as popup', {
                        redirectUrl: finalUrl,
                    });
                    flowActions.completeBookingFlow();
                } else {
                    logger.debug('📧 [Booking Flow] Redirecting now', {
                        redirectUrl: finalUrl,
                    });
                    window.location.href = finalUrl;
                }
            }, 1000);

            return () => clearTimeout(redirectTimer);
        }
    }, [
        flowState.bookingDetails,
        flowState.visitorProfile?.email,
        streamingMessage,
        domain,
        isCopilotLayout,
        onToggleLayout,
        flowActions,
        getPostBookingRedirectUrl,
    ]);

    useEffect(() => {
        // Set to visible after initial animation
        const timer = setTimeout(() => {
            setAnimationState('visible');
        }, 600);
        return () => clearTimeout(timer);
    }, []);

    // Dynamically adapt height on mobile when the on-screen keyboard shows/hides
    useEffect(() => {
        if (typeof window === 'undefined') return;

        const computeHeight = () => {
            const isMobileWidth = (previewViewportWidth ?? window.innerWidth) <= 1242;
            if (!isMobileWidth) {
                setContainerHeight('min(max(67vh, 536px), 600px)');
                return;
            }

            const dvhSupported =
                typeof CSS !== 'undefined' && CSS.supports && CSS.supports('height', '100dvh');

            // Device-preview mode: the widget lives in an iframe, so window.visualViewport
            // reports the host window height, not the simulated device. Use viewport units
            // that resolve against the iframe instead of the parent's visualViewport.
            if (previewViewportWidth != null) {
                setContainerHeight(dvhSupported ? '100dvh' : '100vh');
                return;
            }

            // Prefer visualViewport height when available (adapts to keyboard)
            const vv = window.visualViewport as VisualViewport | undefined;
            if (vv && typeof vv.height === 'number') {
                setContainerHeight(`${Math.round(vv.height)}px`);
                return;
            }

            // Fallback to dynamic viewport units if supported
            // Note: dvh adapts to browser UI changes (iOS 16.4+) but may not react to keyboard on older iOS
            setContainerHeight(dvhSupported ? '100dvh' : '100vh');
        };

        computeHeight();

        const handleResize = () => computeHeight();
        window.addEventListener('resize', handleResize);

        const vv = window.visualViewport as VisualViewport | undefined;
        if (vv) {
            vv.addEventListener('resize', handleResize);
            vv.addEventListener('scroll', handleResize);
        }

        return () => {
            window.removeEventListener('resize', handleResize);
            if (vv) {
                vv.removeEventListener('resize', handleResize);
                vv.removeEventListener('scroll', handleResize);
            }
        };
    }, [previewViewportWidth]);

    // Show CTA after first assistant message appears and persist it
    // BUT: Don't re-enable CTAs during booking flow
    useEffect(() => {
        const hasAssistantMessage = conversationPairs.some((pair) => pair.answer);

        if (hasAssistantMessage && !shouldShowCTAs && !flowState.isBooking) {
            setShouldShowCTAs(true);
        }

        if (conversationPairs.length === 0) {
            setShouldShowCTAs(false);
        }
    }, [conversationPairs, shouldShowCTAs, flowState.isBooking]);

    // Force hide CTAs whenever booking flow is active (handles race conditions)
    useEffect(() => {
        if (flowState.isBooking && shouldShowCTAs) {
            setShouldShowCTAs(false);
        }
    }, [flowState.isBooking, shouldShowCTAs]);

    const handleMinimize = () => {
        setAnimationState('exiting');
        // Delay the actual minimize to allow animation to play
        setTimeout(() => {
            onMinimize();
        }, 300);
    };

    const getAnimationClass = () => {
        switch (animationState) {
            case 'entering':
                return isCopilotLayout ? 'maximizeInCopilot' : 'maximizeIn';
            case 'exiting':
                return isCopilotLayout ? 'minimizeOutCopilot' : 'minimizeOut';
            default:
                return '';
        }
    };

    const userLanguage = useChatLanguage();
    const placeholder = isPostConversion
        ? getSearchBarPlaceholder('POST_CONVERSION', userLanguage)
        : conversationPairs.length === 0
          ? getSearchBarPlaceholder('EXPANDED_NEW_CONVERSATION', userLanguage, siteConfig.name)
          : getSearchBarPlaceholder(
                'EXPANDED_EXISTING_CONVERSATION',
                userLanguage,
                siteConfig.name
            );

    const handleFollowUpClick = (question: string) => {
        onSendMessage(question, 'follow_up_question');
    };

    const handleSuggestedAnswerClick = (answer: string) => {
        onSendMessage(answer, 'suggested_answer');
    };

    // IX-4086: only the header button counts as a user layout switch - the embed
    // flows call onToggleLayout programmatically.
    const handleToggleLayoutClick = useCallback(() => {
        track(
            isCopilotLayout ? 'copilot_layout_exit_clicked' : 'copilot_layout_enter_clicked',
            {},
            'user_action'
        );
        onToggleLayout?.();
    }, [isCopilotLayout, onToggleLayout]);

    // IX-4266: plain-text transcript for the header menu's "Copy transcript".
    // Returns success so the menu can show the "Copied" acknowledgement only
    // when the write actually landed — clipboard access is permission-gated and
    // absent entirely on non-secure origins.
    const handleCopyTranscript = useCallback(
        () => copyTranscript(conversationPairs, streamingMessage?.content, navigator.clipboard),
        [conversationPairs, streamingMessage]
    );

    const handleEmbedBack = useCallback(() => {
        if (layoutBeforeEmbed === 'copilot' && onToggleLayout) {
            onToggleLayout();
        }
        flowActions.hideInChatEmbed();
    }, [flowActions, layoutBeforeEmbed, onToggleLayout]);

    const handleBookingCompleted = useCallback(() => {
        logger.debug('📅 [Booking Flow] In-chat booking completed, sending marker to backend');
        onSendMessage(BOOKING_COMPLETED_MARKER, 'booking_completed');

        // Navigate the visitor to the CTA's configured thank-you page so the existing
        // thank_you_pages conversion tracking + post-conversion qualification flow kicks in.
        // Small delay lets the booking_completed marker request leave the network queue
        // before the page unloads.
        const redirectUrl = flowState.inChatEmbedPostBookingRedirectUrl;
        if (redirectUrl) {
            logger.debug('📅 [Booking Flow] Redirecting to post-booking thank-you page', {
                redirectUrl,
            });
            setTimeout(() => {
                window.location.href = redirectUrl;
            }, 300);
        }
    }, [onSendMessage, flowState.inChatEmbedPostBookingRedirectUrl]);

    // Check if running in Chrome extension mode for visual indicator
    // Users can hide the indicator by clicking on the banner
    const isChromeExtension = getDeploymentTarget() === 'chrome-extension';
    const showChromeIndicator = isChromeExtension && !isChromeIndicatorHidden();

    // Copilot: fixed height for side panel (full viewport minus margins, capped at 740px)
    const copilotHeight = 'min(calc(100vh - 96px), 740px)';

    return (
        <>
            <ExpandedChatAnimationStyles />
            <style>{`
                .chat-expanded-view-centered {
                    transform: translateX(-50%);
                }
                /* Center the absolute overlay with inset + auto margins instead of
                   translate utilities. This keeps the layout intent obvious and avoids
                   the Shadow DOM issue where Tailwind's translate custom properties can
                   invalidate the final centering rule. */
                .chat-expanded-input-overlay {
                    width: 800px;
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    margin-inline: auto;
                    z-index: 1;
                    pointer-events: none;
                }
                .chat-expanded-input-overlay .rose-search-bar-main-content,
                .chat-expanded-input-overlay .rose-search-bar-disclaimer-area,
                .chat-expanded-input-overlay > :not(.rose-search-bar-glass-container) {
                    pointer-events: auto;
                }
                @media (max-width: 852px) {
                    .chat-expanded-input-overlay {
                        width: auto;
                        margin-inline: 0;
                    }
                }
                @media (max-width: 1242px) {
                    .chat-expanded-view-centered.minimizeOut {
                        animation: minimizeOutFullWidth 0.4s cubic-bezier(0.55, 0.055, 0.675, 0.19) forwards;
                    }
                }
                @keyframes minimizeOutFullWidth {
                    0% {
                        opacity: 1;
                        transform: translateX(-50%) translateY(0px) scale(1);
                        border-radius: 16px;
                    }
                    70% {
                        opacity: 1;
                    }
                    100% {
                        opacity: 0;
                        transform: translateX(-50%) translateY(-20px) scale(0.1);
                        border-radius: 50px;
                    }
                }
            `}</style>
            <div
                className={`chat-expanded-view ${getAnimationClass()}
                    fixed
                    ${
                        isCopilotLayout
                            ? `right-8 bottom-8 w-[440px] max-[480px]:w-[calc(100vw-40px)] max-[480px]:right-5`
                            : `bottom-0 left-1/2 w-[1080px] max-[1242px]:w-[calc(100vw)] max-[1242px]:max-w-[calc(100vw)] ${animationState === 'visible' ? 'chat-expanded-view-centered' : ''}`
                    }`}
                data-ph-capture-attribute-view-type="expanded-chat"
                data-ph-capture-attribute-form-factor={formFactor}
                data-ph-capture-attribute-site={siteConfig.name}
                data-ph-capture-attribute-messages-count={conversationPairs.length}
                data-ph-capture-attribute-animation-state={animationState}
                style={{
                    zIndex,
                    height: isCopilotLayout
                        ? copilotHeight
                        : (containerHeight ??
                          (typeof window !== 'undefined' &&
                          (previewViewportWidth ?? window.innerWidth) <= 1242
                              ? typeof CSS !== 'undefined' &&
                                CSS.supports &&
                                CSS.supports('height', '100dvh')
                                  ? '100dvh'
                                  : '100vh'
                              : 'min(max(67vh, 536px), 600px)')),
                    bottom: visualViewportBottomOffset
                        ? `${(isCopilotLayout ? 32 : 0) + visualViewportBottomOffset}px`
                        : undefined,
                    opacity: animationState === 'visible' ? 1 : undefined,
                    pointerEvents: 'auto', // Ensure clicks work in Shadow DOM with pointer-events: none parent
                }}
            >
                <div
                    className={`chat-expanded-container relative h-full flex flex-col items-center p-0 bg-white
                        transition-all duration-300 ease-in-out
                        ${
                            isCopilotLayout
                                ? 'w-full overflow-hidden'
                                : 'max-[1242px]:w-full shadow-panel'
                        }`}
                    style={{
                        backgroundColor: 'var(--rw-surface-panel)',
                        backgroundImage:
                            'linear-gradient(var(--rw-bg-panel-tint), var(--rw-bg-panel-tint))',
                        // Chrome extension visual indicator (can be hidden by clicking the banner)
                        ...(showChromeIndicator && {
                            boxShadow: '0 0 0 3px #dc2626, 0 0 12px rgba(220, 38, 38, 0.4)',
                        }),
                        // Copilot layout: add border and shadow per Figma design
                        ...(isCopilotLayout &&
                            !showChromeIndicator && {
                                boxShadow: 'var(--rw-shadow-shell-side)',
                            }),
                        border: '1px solid var(--rw-border-subtle)',
                        borderRadius: isCopilotLayout
                            ? 'var(--rw-radius-shell-panel)'
                            : 'var(--rw-radius-shell-panel) var(--rw-radius-shell-panel) 0 0',
                    }}
                >
                    {/* Header — replaced by top bar during post-conversion or booking capture */}
                    {(() => {
                        const profiling = getLatestProfilingProgress(conversationPairs);
                        if (isPostConversion && profiling && !profiling.is_complete) {
                            return (
                                <PostConversionTopBar
                                    onMinimize={handleMinimize}
                                    viewLayout={displayedViewLayout}
                                    onToggleLayout={handleToggleLayoutClick}
                                    showLayoutToggle={
                                        !inChatEmbedUrl &&
                                        !isFormAssistant &&
                                        formFactor === 'default' &&
                                        !!onToggleLayout &&
                                        !isMobileDevice()
                                    }
                                    currentQuestion={profiling.current_question}
                                    totalFields={profiling.total_fields}
                                    answeredFields={profiling.answered_fields}
                                />
                            );
                        }
                        if (flowState.bookingDetails?.isAwaitingEmail) {
                            return (
                                <BookingTopBar
                                    onMinimize={handleMinimize}
                                    viewLayout={displayedViewLayout}
                                    onToggleLayout={handleToggleLayoutClick}
                                    showLayoutToggle={
                                        !inChatEmbedUrl &&
                                        !isFormAssistant &&
                                        formFactor === 'default' &&
                                        !!onToggleLayout &&
                                        !isMobileDevice()
                                    }
                                    pendingFieldId={flowState.bookingDetails.pendingFieldId}
                                />
                            );
                        }
                        return (
                            <ExpandedChatHeader
                                onMinimize={handleMinimize}
                                viewLayout={displayedViewLayout}
                                onToggleLayout={handleToggleLayoutClick}
                                showLayoutToggle={
                                    !inChatEmbedUrl &&
                                    !isFormAssistant &&
                                    formFactor === 'default' &&
                                    !!onToggleLayout &&
                                    !isMobileDevice()
                                }
                                showExitCopilot={!!onExitCopilotMode}
                                onExitCopilotMode={onExitCopilotMode}
                                showConversationMenu={conversationPairs.length > 0}
                                onResetConversation={onResetConversation}
                                onCopyTranscript={handleCopyTranscript}
                                showBackButton={!!inChatEmbedUrl}
                                onBack={handleEmbedBack}
                            />
                        );
                    })()}

                    {/* Chat Messages Area — calendar embed renders inside as a message card */}
                    <ExpandedChatContainer
                        conversationPairs={conversationPairs}
                        streamingMessage={streamingMessage}
                        isLoading={isLoading}
                        primaryColor={primaryColor}
                        siteConfig={siteConfig}
                        messagesEndRef={messagesEndRef}
                        evaluationEnabled={evaluationEnabled}
                        domain={domain}
                        onLatestQuestionRef={onLatestQuestionRef}
                        onFollowUpClick={handleFollowUpClick}
                        onSuggestedAnswerClick={handleSuggestedAnswerClick}
                        onRetry={onRetry}
                        hideInlineCTAs={hideInlineCTAs}
                        viewLayout={displayedViewLayout}
                        showInlineDemoCTA={
                            inlineCTAOnDemo && lastMessageHasDemoOffer && !ctasDisabledByMode
                        }
                        isCtaPage={isOnCTADestinationPage}
                        inlineCtaOptionEnabled={inlineCTAOnDemo}
                        visitorEmail={flowState.visitorProfile?.email}
                        onBookingFlowStart={handleBookingFlowStart}
                        trailingContentHeight={
                            inChatEmbedUrl ? getInChatCalendarRenderedHeight() : undefined
                        }
                        bottomInset={isCopilotLayout ? 0 : inputAreaHeight}
                        viewportResizeKey={viewportResizeKey}
                        hideInlineProfilingProgress={isPostConversion}
                        isPostConversion={isPostConversion}
                        calendarEmbed={
                            inChatEmbedUrl ? (
                                <InChatCalendarEmbed
                                    url={inChatEmbedUrl}
                                    onBack={handleEmbedBack}
                                    onBookingCompleted={handleBookingCompleted}
                                    primaryColor={primaryColor}
                                />
                            ) : undefined
                        }
                    />

                    {/* Input Area — always visible, disabled during calendar embed */}
                    <div
                        ref={inputAreaRef}
                        className={`chat-input-area flex flex-col gap-2 transition-all duration-300 ease-in-out mb-0
                            ${isCopilotLayout ? 'w-full' : 'chat-expanded-input-overlay'}`}
                    >
                        {/* Keep the expanded shell above the scroller so the glass surface
                            frosts the chat underneath instead of sitting on the panel. */}
                        {flowState.bookingDetails?.isAwaitingEmail && <BookingEmailDisclaimer />}
                        <SearchBar
                            onSendMessage={onSendMessage}
                            isLoading={!!inChatEmbedUrl || isLoading}
                            primaryColor={primaryColor}
                            placeholder={placeholder}
                            showPlaceholder={true}
                            isExpanded={true}
                            domain={domain}
                            data-ph-capture-attribute-input-area="expanded-chat-message"
                            showCtas={
                                !inChatEmbedUrl &&
                                shouldShowCTAs &&
                                !ctasDisabledByMode &&
                                !(inlineCTAOnDemo && lastMessageHasDemoOffer && !hideInlineCTAs)
                            }
                            ctas={ctasDisabledByMode ? undefined : stripCTAs}
                            showDisclaimer={true}
                            compactDisclaimer={isCopilotLayout}
                            showBorder={!isCopilotLayout}
                            isCopilotLayout={isCopilotLayout}
                            isCtaPage={isOnCTADestinationPage}
                            hadDemoOffer={lastMessageHasDemoOffer}
                            inlineCtaOptionEnabled={inlineCTAOnDemo}
                            autoFocus={!inChatEmbedUrl && !isMobileDevice()}
                            visitorEmail={flowState.visitorProfile?.email}
                            onBookingFlowStart={handleBookingFlowStart}
                            onMinimize={handleMinimize}
                            onEngage={onEngage}
                            forceHideCTAs={
                                !!inChatEmbedUrl || flowState.bookingDetails?.isAwaitingEmail
                            }
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default ExpandedChatView;
